import Container, { Service } from 'typedi';
import { HttpException } from '../../../utils/exceptions/httpException';
import { TFunction } from 'i18next';
import { status } from '../../../utils/helpers/api.responses';
import { AllocationRequestStatus, AllocationRequestStatusEnum, IAllocationRequest } from '../interfaces/allocationRequest.interface';
import User from 'Modules/Auth/models/auth.model';
import mongoose, { Types, ClientSession } from 'mongoose';
import AllocationRequest from '../models/allocationRequest.model';
import Labour from '../../../Modules/Labour/models/labour.model';
import Availability from '../../../Modules/availabilityManagement/models/availability.model';
import { AvailableStatusEnum } from '../../../Modules/Labour/interfaces/labour.interface';
import { RolesEnum } from '../../../Modules/Auth/interfaces/auth.interface';
import { NotificationService } from '../../../Modules/Notification/services/notification.service';
import Attachment from '../../../Modules/Attachment/models/attachment.model';
import { aws } from '../../../config/index';
import Job from '../../../Modules/jobManagement/models/job.model';
import JobService from '../../../Modules/jobManagement/services/job.service';
import Role from 'Modules/Role/models/role.model';

interface Role {
    _id: Types.ObjectId;
    name: string;
}

@Service()
export class AllocationRequestService {
    public notification = Container.get(NotificationService);
    public jobService = Container.get(JobService)

    private async sendAllocationNotification(
        action: string,
        data: { from: Types.ObjectId; to: Types.ObjectId | Types.ObjectId[]; entityData: any },
        t: TFunction
    ) {
        try {
            await this.notification.sendNotification('allocationRequest', action, data, t);
        } catch (error) {
            console.error(`Notification failed for action ${action}:`, error);
        }
    }

    public async createAllocationRequest(data: Partial<IAllocationRequest>, t: TFunction) {
        const { userIds, message, startDate, endDate, jobId, wage, createdBy, allocationNotes } = data;
        if (!userIds?.length || !startDate || !endDate || !jobId || !createdBy) {
            throw new HttpException(
                status.BadRequest,
                t('General.required', { field: 'userIds, startDate, endDate, jobId, createdBy' })
            );
        }

        if (new Date(startDate) > new Date(endDate)) {
            throw new HttpException(status.BadRequest, t('General.invalid', { field: 'endDate must be after startDate' }));
        }

        const session = await mongoose.startSession();
        let transactionCommitted = false;

        try {
            session.startTransaction();
            const users = await User.find({ _id: { $in: userIds } })
                .select('firstName lastName email roleId')
                .session(session)
                .lean()
            const createdByUser = await User.findById(createdBy)
                .select('firstName lastName email roleId')
                .session(session)
                .lean()
            const labours = await Labour.find({ userId: { $in: userIds }, isDeleted: false })
                .session(session)
                .lean()
            const job = await Job.findById(jobId)
                .session(session)
                .lean()

            if (users.length !== userIds.length) {
                throw new HttpException(status.BadRequest, t('General.not_found', { field: t('User.user') }));
            }

            if (!createdByUser) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.createdBy') }));
            }

            if (!job || job.isDeleted || job.status === 'Closed') {
                throw new HttpException(status.BadRequest, t('General.not_found', { field: t('Job.job') }));
            }

            const roleIds = users.map(u => u.roleId);
            const roles = await Role.find({ _id: { $in: roleIds } }).session(session).lean();
            const invalidUsers = users.filter(u => {
                const role = roles.find(r => r._id.toString() === u.roleId.toString());
                return !role || role.name.toLowerCase() !== RolesEnum.LABOUR.toLowerCase();
            });
            if (invalidUsers.length > 0) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: t('User.labour') }));
            }

            const createdByRole = await Role.findById(createdByUser.roleId).session(session).lean();
            if (
                !createdByRole ||
                ![RolesEnum.ADMIN.toLowerCase(), RolesEnum.STAFF.toLowerCase()].includes((createdByRole.name as any).toLowerCase())
            ) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            if (labours.length !== userIds.length) {
                const missing = userIds.filter(id => !labours.some(l => l.userId.toString() === id.toString()));
                throw new HttpException(
                    status.BadRequest,
                    t('General.not_found', { field: `${t('Labour.labour')} (${missing.join(', ')})` })
                );
            }


            const validUserIds: Types.ObjectId[] = [];

            for (const userId of userIds) {
                const unavailableLabour = await Availability.findOne({
                    userId,
                    startDate: { $lte: new Date(endDate) },
                    endDate: { $gte: new Date(startDate) },
                    isDeleted: false,
                    status: AvailableStatusEnum.UNAVAILABLE,
                })
                    .select('userId')
                    .session(session)
                    .lean()
                const existingRequest = await AllocationRequest.findOne({
                    userId,
                    startDate: { $lte: new Date(endDate) },
                    endDate: { $gte: new Date(startDate) },
                    isDeleted: false,
                    status: AllocationRequestStatusEnum.PENDING,
                })
                    .select('_id')
                    .session(session)
                    .lean()


                const user = users.find(u => u._id.toString() === userId.toString());
                const name = user?.firstName || user?.email || 'User';

                if (unavailableLabour) {
                    throw new HttpException(
                        status.BadRequest,
                        t('Availability.LabourUnavailable', { field: `${t('Labour.labour')} (${name})` })
                    );
                }

                if (existingRequest) {
                    throw new HttpException(
                        status.BadRequest,
                        t('General.already_exist', { field: `${t('AllocationRequest.allocationRequest')} for ${name}` })
                    );
                }

                validUserIds.push(userId);
            }




            const allocationRequests = await Promise.all(
                validUserIds.map(async uid => {
                    const allocationRequest = new AllocationRequest({
                        userId: uid,
                        message,
                        startDate,
                        endDate,
                        jobId,
                        wage,
                        createdBy,
                        allocationNotes,
                        status: AllocationRequestStatusEnum.PENDING,
                    });
                    await allocationRequest.save({ session });
                    return allocationRequest;
                })
            );

            if (allocationRequests.length > 0) {
                await Labour.updateMany(
                    { userId: { $in: validUserIds }, isDeleted: false },
                    { $push: { workInvitations: { $each: allocationRequests.map(r => r._id) } } },
                    { session }
                );
            }
            const labourMap = new Map(labours.map(l => [l.userId.toString(), l.employee_id]));
            await session.commitTransaction();
            transactionCommitted = true;
            const notificationErrors: Error[] = [];
            // await Promise.all(
            //     users
            //         .filter(u => validUserIds.some(vid => vid.toString() === u._id.toString()))
            //         .map(async (user, index) => {
            //             await this.sendAllocationNotification(
            //                 'newAllocationRequest',
            //                 {
            //                     from: new Types.ObjectId(createdBy),
            //                     to: new Types.ObjectId(user._id),
            //                     entityData: {
            //                         userId: user._id,
            //                         allocationRequestId: allocationRequests[index]._id,
            //                         location: job.location,
            //                         companyName: job.companyName,
            //                         wage,
            //                         startDate: new Date(startDate).toLocaleDateString(),
            //                         endDate: new Date(endDate).toLocaleDateString(),
            //                         status: AllocationRequestStatusEnum.PENDING,
            //                         message: message || 'You have a new allocation request',
            //                         firstName: user.firstName || user.email || 'User',
            //                         lastName: user.lastName || '',
            //                         responderName: createdByUser.firstName || createdByUser.email || 'Creator',
            //                         responseNotes: '',
            //                         labourId: labourMap.get(user._id.toString()) || 'N/A',
            //                         allocationNotes: allocationNotes || ''
            //                     },
            //                 },
            //                 t
            //             );
            //         })
            // );

            // if (createdByRole.name.toLowerCase() === RolesEnum.STAFF.toLowerCase()) {
            //     const adminUsers = await User.find({ isDeleted: false })
            //         .populate({
            //             path: 'roleId',
            //             match: { name: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') },
            //             select: 'name',
            //         })
            //         .select('_id')
            //         .lean();
            //     const adminIds = adminUsers.filter(u => u.roleId).map(u => new Types.ObjectId(u._id));

            //     if (adminIds.length > 0) {
            //         const userNames = users
            //             .filter(u => validUserIds.some(vid => vid.toString() === u._id.toString()))
            //             .map(u => `${u.firstName || u.email || 'User'} ${u.lastName || ''}`.trim())
            //             .join(', ');
            //         await this.sendAllocationNotification(
            //             'newAllocationRequest',
            //             {
            //                 from: new Types.ObjectId(createdBy),
            //                 to: adminIds,
            //                 entityData: {
            //                     userId: validUserIds,
            //                     allocationRequestId: allocationRequests.map(r => r._id).join(', '),
            //                     location: job?.location || 'Unknown',
            //                     companyName: job?.companyName || 'N/A',
            //                     wage: wage || 'N/A',
            //                     startDate: new Date(startDate).toLocaleDateString(),
            //                     endDate: new Date(endDate).toLocaleDateString(),
            //                     status: AllocationRequestStatusEnum.PENDING,
            //                     message: message || `A new allocation request has been created for ${userNames}.`,
            //                     firstName: userNames,
            //                     lastName: '',
            //                     responderName: createdByUser?.firstName || createdByUser?.email || 'Creator',
            //                     responseNotes: '',
            //                     labourId: validUserIds.map(id => labourMap.get(id.toString()) || 'N/A').join(', '),
            //                     allocationNotes: allocationNotes || ''
            //                 },
            //             },
            //             t
            //         );
            //     }
            // }

            if (notificationErrors.length > 0) {
                console.warn(`Some notifications failed: ${notificationErrors.length} errors`, notificationErrors);
            }

            return allocationRequests;
        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            throw error instanceof HttpException
                ? error
                : new HttpException(
                    status.InternalServerError,
                    t('General.errorCreating', { field: t('AllocationRequest.allocationRequest') })
                );
        } finally {
            await session.endSession();
        }
    }


    public async getAllocationRequestById(id: string, t: TFunction) {
        try {
            const allocationRequest = await AllocationRequest.findOne({ _id: id, isDeleted: false })
                .populate({
                    path: 'userId',
                    select: 'firstName lastName email profileImage phone countryCode',
                    populate: {
                        path: 'profileImage',
                        model: 'Attachment',
                        select: 'key filename fileSize',
                    },
                })
                .populate('createdBy', 'firstName lastName')
                .populate('jobId', 'location companyName note status')
                .lean();
            if (!allocationRequest) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('AllocationRequest.allocationRequest') }));
            }
            const labour = await Labour.findOne({ userId: allocationRequest.userId, isDeleted: false })
                .select('employee_id skills')
                .lean();

            const profileImageData = (allocationRequest.userId as any).profileImage
                ? {
                    _id: (allocationRequest.userId as any).profileImage._id.toString(),
                    filename: (allocationRequest.userId as any).profileImage.filename || null,
                    url: (allocationRequest.userId as any).profileImage.key
                        ? `${process.env.S3_BASE_URL}/${(allocationRequest.userId as any).profileImage.key}`
                        : null,
                    fileSize: (allocationRequest.userId as any).profileImage.fileSize
                }
                : null;

            return {
                _id: allocationRequest._id,
                requestedDte: allocationRequest.requestDate,
                location: (allocationRequest.jobId as any).location,
                companyName: (allocationRequest.jobId as any).companyName,
                startDate: allocationRequest.startDate,
                message: allocationRequest.message,
                endDate: allocationRequest.endDate,
                wages: allocationRequest.wage,
                note: (allocationRequest.jobId as any).note,
                email: (allocationRequest.userId as any).email,
                firstName: (allocationRequest.userId as any).firstName,
                lastName: (allocationRequest.userId as any).lastName,
                jobStatus: (allocationRequest.jobId as any).status,
                status: allocationRequest.status,
                profileImage: profileImageData,
                phone: (allocationRequest.userId as any).phone,
                countryCode: (allocationRequest.userId as any).countryCode,
                allocationNotes: allocationRequest.allocationNotes
            };

        } catch (error) {
            if (error instanceof HttpException) throw error;
            if (error instanceof mongoose.Error.CastError) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'id' }));
            }
            throw new HttpException(status.InternalServerError, t('General.errorFetching', { field: t('AllocationRequest.allocationRequest') }));
        }
    }

    public async getAllAllocationRequests(filters: any, page: number, limit: number, sort: any, t: TFunction) {
        try {
            const skip = (page - 1) * limit;
            filters.isDeleted = false;
            if (filters.status && !Object.values(AllocationRequestStatusEnum).includes(filters.status)) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'status' }));
            }
            if (filters.userId) {
                filters.userId = Array.isArray(filters.userId)
                    ? { $in: filters.userId.map((id: string) => new Types.ObjectId(id)) }
                    : new Types.ObjectId(filters.userId);
            }

            const [results, totalCount, statusCounts] = await Promise.all([
                AllocationRequest.find(filters)
                    .select('status userId startDate endDate wage requestDate responseNotes')
                    .populate('userId', 'firstName lastName email countryCode profileImage phone')
                    .populate('createdBy', 'firstName lastName')
                    .populate('jobId', 'location companyName status note')
                    .sort(sort)
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                AllocationRequest.countDocuments(filters),
                AllocationRequest.aggregate([{ $match: filters }, { $group: { _id: '$status', count: { $sum: 1 } } }]),
            ]);

            const profileImageIds = results
                .filter(r => (r.userId as any).profileImage)
                .map(r => (r.userId as any).profileImage._id.toString());


            const attachments = profileImageIds.length > 0
                ? await Attachment.find({ _id: { $in: profileImageIds.map(id => new Types.ObjectId(id)) } })
                    .select('key filename fileSize')

                    .lean()
                : [];
            const attachmentMap = new Map(attachments.map(a => [a._id.toString(), { key: a.key, filename: a.filename }]));

            const requestsWithLabour = results.map(request => {
                const profileImageObj = (request.userId as any).profileImage
                    ? attachmentMap.get((request.userId as any).profileImage._id.toString())
                    : null;
                const profileImageData = profileImageObj
                    ? {
                        _id: (request.userId as any).profileImage._id.toString(),
                        filename: profileImageObj.filename || null,
                        url: profileImageObj.key ? `${aws.S3_BASE_URL}/${profileImageObj.key}` : null,
                        fileSize: (request.userId as any).profileImage.fileSize
                    }
                    : null;

                return {
                    _id: request._id,
                    userId: request.userId._id,
                    responseNotes: request.responseNotes,
                    requestedDte: request.requestDate,
                    companyName: (request.jobId as any).companyName,
                    startDate: request.startDate,
                    endDate: request.endDate,
                    wages: request.wage,
                    location: (request.jobId as any).location,
                    jobStatus: (request.jobId as any).status,
                    jobNote: (request.jobId as any).note,
                    email: (request.userId as any).email,
                    firstName: (request.userId as any).firstName,
                    lastName: (request.userId as any).lastName,
                    status: request.status,
                    profileImage: profileImageData,
                    phone: (request.userId as any).phone,
                    countryCode: (request.userId as any).countryCode,
                    allocationNotes: request.allocationNotes
                };
            });

            const counts: { [key: string]: number } = {
                pending: 0,
                accepted: 0,
                rejected: 0,
            };
            statusCounts.forEach((item: { _id: string; count: number }) => {
                counts[item._id.toLowerCase()] = item.count;
            });

            return {
                data: requestsWithLabour,
                total: totalCount,
                page,
                totalPage: Math.ceil(totalCount / limit),
                statusCounts: counts,
            };
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('AllocationRequest.allocationRequest') })
            );
        }
    }

    public async respondToAllocationRequest(
        id: string,
        allocationStatus: AllocationRequestStatus,
        responseNotes: string,
        updatedBy: string,
        t: TFunction
    ) {

        const session = await mongoose.startSession();
        let transactionCommitted = false
        try {
            session.startTransaction()
            const allocationRequest = await AllocationRequest.findOne({ _id: id, isDeleted: false })
                .populate({
                    path: 'userId',
                    select: 'firstName lastName email roleId',
                    populate: { path: 'roleId', select: 'name' },
                })
                .session(session)
            const respondingUser = await User.findById(updatedBy).populate('roleId', 'name').session(session).lean()

            if (!allocationRequest) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('AllocationRequest.allocationRequest') }));
            }
            if (allocationRequest.status.toLowerCase() !== AllocationRequestStatusEnum.PENDING.toLowerCase()) {
                throw new HttpException(
                    400,
                    t('AllocationRequest.already_responded')
                );
            }
            if (!respondingUser || !respondingUser.roleId) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.user') }));
            }

            const createdByUser = await User.findById(allocationRequest.createdBy).select('email firstName lastName companyName').session(session).lean()
            const labour = await Labour.findOne({ userId: allocationRequest.userId, isDeleted: false }).select('employee_id').session(session).lean()

            if (!createdByUser) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.createdBy') }));
            }
            if (!labour) {
                throw new HttpException(status.BadRequest, t('General.not_found', { field: t('Labour.labour') }));
            }
            const isLabourer = (respondingUser.roleId as Role).name.toLowerCase() === RolesEnum.LABOUR.toLowerCase();
            const isAssignedUser = allocationRequest.userId._id.toString() === updatedBy.toString();

            if (!isLabourer || !isAssignedUser) {
                throw new HttpException(status.Forbidden, t('Role.permission_denied'));
            }

            const normalizedStatus = Object.values(AllocationRequestStatusEnum).find(
                s => s.toLowerCase() === allocationStatus.toLowerCase()
            );
            if (!normalizedStatus) {
                throw new HttpException(status.BadRequest, t('General.invalid_enum', { field: 'allocationStatus' }));
            }

            // if (allocationStatus.toLowerCase() === AllocationRequestStatusEnum.REJECTED.toLowerCase() && !responseNotes) {
            //     throw new HttpException(status.BadRequest, t('General.required', { field: 'responseNotes for rejected status' }));
            // }

            allocationRequest.status = allocationStatus as AllocationRequestStatus;
            allocationRequest.responseNotes = responseNotes;
            allocationRequest.respondedAt = new Date();
            allocationRequest.updatedBy = new Types.ObjectId(updatedBy);
            await allocationRequest.save({ session });

            const job = await Job.findById(allocationRequest.jobId).session(session);
            if (allocationStatus.toLowerCase() === AllocationRequestStatusEnum.ACCEPTED.toLowerCase()) {
                if (!job || job.isDeleted || job.status === 'Closed') {
                    throw new HttpException(status.BadRequest, t('General.not_found', { field: t('Job.job') }));
                }
                if (job.open <= 0) {
                    throw new HttpException(status.BadRequest, 'No open spots available for this job');
                }
                job.allotted += 1;
                job.open = job.noOfSpots - job.allotted;


                if (job.open === 0) {
                    job.status = 'Closed';
                    // await this.jobService.closeJob(job._id.toString(), t,session);
                }
                await job.save({ session });
                const existingAvailability = await Availability.findOne({
                    userId: allocationRequest.userId,
                    startDate: { $lte: allocationRequest.endDate },
                    endDate: { $gte: allocationRequest.startDate },
                    status: AvailableStatusEnum.UNAVAILABLE,
                    isDeleted: false,
                }).session(session);

                if (existingAvailability) {
                    throw new HttpException(status.ResourceExist, t('General.already_exist', { field: t('Availability.availability') }));
                }

                const availability = new Availability({
                    userId: allocationRequest.userId,
                    startDate: allocationRequest.startDate,
                    endDate: allocationRequest.endDate,
                    status: AvailableStatusEnum.UNAVAILABLE,
                    jobId: new Types.ObjectId(allocationRequest.jobId),
                    createdBy: new Types.ObjectId(updatedBy),
                    updatedBy: new Types.ObjectId(updatedBy),
                });
                await availability.save({ session });

                await Labour.updateOne(
                    { userId: allocationRequest.userId, isDeleted: false },
                    {
                        $push: { availability: availability._id },
                        availability_status: AvailableStatusEnum.UNAVAILABLE,
                    },
                    { session }
                );
            }
            await session.commitTransaction()
            transactionCommitted = true

            const recipients = [allocationRequest.userId._id, allocationRequest.createdBy];
            // await this.sendAllocationNotification(
            //     'respondAllocationRequest',
            //     {
            //         from: new Types.ObjectId(updatedBy),
            //         to: recipients,
            //         entityData: {
            //             userId: allocationRequest.userId._id,
            //             allocationRequestId: allocationRequest._id,
            //             location: (job as any).location,
            //             wage: allocationRequest.wage,
            //             companyName: (job as any).companyName,
            //             startDate: allocationRequest.startDate,
            //             endDate: allocationRequest.endDate,
            //             status: allocationStatus.toLowerCase(),
            //             responseNotes: responseNotes || '',
            // allocationNotes: allocationNotes || '',
            //             firstName: (allocationRequest.userId as any).firstName,
            //             lastName: (allocationRequest.userId as any).lastName,
            //             labourId: labour?.employee_id || 'N/A',
            //         },
            //     },
            //     t
            // );

            const adminAndStaffUsers = await User.find({ isDeleted: false })
                .populate({
                    path: 'roleId',
                    match: {
                        $or: [
                            { name: { $regex: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') } },
                            { name: { $regex: new RegExp(`^${RolesEnum.STAFF}$`, 'i') } },
                        ],
                    },
                    select: 'name',
                })
                .select('_id')
                .lean();
            const adminStaffIds = adminAndStaffUsers.filter(u => u.roleId).map(u => new Types.ObjectId(u._id));

            // if (adminStaffIds.length > 0) {
            //     await this.sendAllocationNotification(
            //         'respondAllocationRequest',
            //         {
            //             from: new Types.ObjectId(updatedBy),
            //             to: adminStaffIds,
            //             entityData: {
            //                 userId: allocationRequest.userId._id,
            //                 allocationRequestId: allocationRequest._id,
            //                 location: (job as any).location,
            //                 wage: allocationRequest.wage,
            //                 companyName: (job as any).companyName,
            //                 startDate: allocationRequest.startDate,
            //                 endDate: allocationRequest.endDate,
            //                 status: allocationStatus,
            // allocationNotes: allocationNotes || '',
            //                 responseNotes: responseNotes || '',
            //                 firstName: (allocationRequest.userId as any).firstName,
            //                 lastName: (allocationRequest.userId as any).lastName,
            //                 labourId: labour?.employee_id || 'N/A',
            //             },
            //         },
            //         t
            //     );
            // }

            return allocationRequest;

        } catch (error) {
            console.log(error)
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.InternalServerError,
                t('General.errorUpdating', { field: t('AllocationRequest.allocationRequest') })
            );
        } finally {
            await session.endSession();
        }
    }

    public async updateAllocationRequest(id: string, data: Partial<IAllocationRequest>, t: TFunction) {

        const session = await mongoose.startSession();
        let transactionCommitted = false

        try {
            session.startTransaction()
            const allocationRequest = await AllocationRequest.findOne({ _id: id, isDeleted: false })
                .populate({
                    path: 'userId',
                    select: 'firstName lastName email roleId',
                    populate: { path: 'roleId', select: 'name' },
                })
                .populate({
                    path: 'createdBy',
                    select: 'firstName lastName email',
                })
                .populate('jobId', 'location companyName')
                .session(session);
            if (!allocationRequest) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('AllocationRequest.allocationRequest') }));
            }

            const updateFields = Object.keys(data).filter(key => key !== 'updatedBy' && key !== 'updatedAt');
            if (updateFields.length === 0) {
                throw new HttpException(status.BadRequest, t('General.required', { field: 'at least one update field' }));
            }

            const updateData: Partial<IAllocationRequest> = {};
            for (const key of updateFields) {
                if (data[key] !== undefined) {
                    updateData[key] = data[key];
                }
            }

            if (updateData.startDate || updateData.endDate) {
                const start = updateData.startDate ?? allocationRequest.startDate;
                const end = updateData.endDate ?? allocationRequest.endDate;
                if (end < start) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: 'endDate must be after startDate' }));
                }

                const existingAvailability = await Availability.findOne({
                    userId: updateData.userId ?? allocationRequest.userId,
                    startDate: { $lte: end },
                    endDate: { $gte: start },
                    status: AvailableStatusEnum.UNAVAILABLE,
                    isDeleted: false,
                    _id: { $ne: allocationRequest._id },
                }).session(session);
                if (existingAvailability) {
                    throw new HttpException(status.BadRequest, t('General.already_exist', { field: t('Availability.availability') }));
                }
                const job = allocationRequest.jobId as any;
                if (job.status === 'Closed') {
                    throw new HttpException(status.BadRequest, t('General.not_found', { field: t('Job.job') }));
                }
            }

            if (updateData.userId) {
                const user = await User.findById(updateData.userId).populate('roleId', 'name').session(session).lean();
                if (!user) {
                    throw new HttpException(status.BadRequest, t('General.not_found', { field: t('User.user') }));
                }
                if ((user.roleId as Role).name.toLowerCase() !== RolesEnum.LABOUR.toLowerCase()) {
                    throw new HttpException(status.BadRequest, t('General.invalid', { field: t('User.labour') }));
                }
                await Labour.updateOne(
                    { userId: allocationRequest.userId, isDeleted: false },
                    { $pull: { workInvitations: allocationRequest._id } },
                    { session }
                );
                await Labour.updateOne(
                    { userId: updateData.userId, isDeleted: false },
                    { $push: { workInvitations: allocationRequest._id } },
                    { session }
                );
            }

            const updatedByUser = await User.findById(data.updatedBy)
                .select('firstName lastName email roleId')
                .populate('roleId', 'name')
                .session(session)
                .lean();
            if (!updatedByUser || !updatedByUser.roleId) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.updatedBy') }));
            }
            const labour = await Labour.findOne({ userId: allocationRequest.userId, isDeleted: false })
                .select('employee_id')
                .session(session)
                .lean();
            updateData.updatedBy = new Types.ObjectId(data.updatedBy);
            updateData.updatedAt = new Date();

            Object.assign(allocationRequest, updateData);
            await allocationRequest.save({ session });
            await session.commitTransaction();
            transactionCommitted = true;
            // await this.sendAllocationNotification(
            //     'updateAllocationRequest',
            //     {
            //         from: new Types.ObjectId(data.updatedBy),
            //         to: [allocationRequest.userId._id, allocationRequest.createdBy._id],
            //         entityData: {
            //             userId: allocationRequest.userId._id,
            //             allocationRequestId: allocationRequest._id,
            //             location: (allocationRequest.jobId as any).location,
            //             wage: updateData.wage ?? allocationRequest.wage,
            //             companyName: (allocationRequest.jobId as any).companyName,
            //             startDate: updateData.startDate ?? allocationRequest.startDate,
            //             endDate: updateData.endDate ?? allocationRequest.endDate,
            //             status: allocationRequest.status,
            //             responseNotes: updateData.responseNotes ?? allocationRequest.responseNotes ?? '',
            //             updatedFields: updateFields.join(', '),
            //             firstName: (allocationRequest.userId as any).firstName,
            //             lastName: (allocationRequest.userId as any).lastName,
            //             labourId: labour?.employee_id || 'N/A',
            //             allocationNotes: updateData.allocationNotes ?? allocationRequest.allocationNotes ?? '',
            //         },
            //     },
            //     t
            // );

            // if ((updatedByUser.roleId as Role).name.toLowerCase() === RolesEnum.STAFF.toLowerCase()) {
            //     const adminUsers = await User.find({ isDeleted: false })
            //         .populate({
            //             path: 'roleId',
            //             match: { name: { $regex: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') } },
            //             select: 'name',
            //         })
            //         .select('_id')
            //         .lean();
            //     const adminIds = adminUsers.filter(u => u.roleId).map(u => new Types.ObjectId(u._id));

            //     if (adminIds.length > 0) {
            //         await this.sendAllocationNotification(
            //             'updateAllocationRequest',
            //             {
            //                 from: new Types.ObjectId(data.updatedBy),
            //                 to: adminIds,
            //                 entityData: {
            //                     userId: allocationRequest.userId._id,
            //                     allocationRequestId: allocationRequest._id,
            //                     location: (allocationRequest.jobId as any).location,
            //                     companyName: (allocationRequest.jobId as any).companyName,
            //                     wage: updateData.wage ?? allocationRequest.wage,
            //                     startDate: updateData.startDate ?? allocationRequest.startDate,
            //                     endDate: updateData.endDate ?? allocationRequest.endDate,
            //                     status: allocationRequest.status,
            //                     responseNotes: updateData.responseNotes ?? allocationRequest.responseNotes ?? '',
            //                     updatedFields: updateFields.join(', '),
            //                     firstName: (allocationRequest.userId as any).firstName,
            //                     lastName: (allocationRequest.userId as any).lastName,
            //                     labourId: labour?.employee_id || 'N/A',
            //                     allocationNotes: updateData.allocationNotes ?? allocationRequest.allocationNotes ?? '',
            //                 },
            //             },
            //             t
            //         );
            //     }
            // }

            return allocationRequest;

        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.InternalServerError,
                t('General.errorUpdating', { field: t('AllocationRequest.allocationRequest') })
            );
        } finally {
            await session.endSession();
        }
    }

    public async getAllocationHistory(filters: any, page: number, limit: number, sort: any, t: TFunction) {

        try {
            const skip = (page - 1) * limit;
            filters.isDeleted = false;
            if (filters.userId) {
                filters.userId = Array.isArray(filters.userId)
                    ? { $in: filters.userId.map((id: string) => new Types.ObjectId(id)) }
                    : new Types.ObjectId(filters.userId);
            }
            filters.status = {
                $in: [AllocationRequestStatusEnum.ACCEPTED, AllocationRequestStatusEnum.REJECTED],
            };

            const [results, totalCount] = await Promise.all([
                AllocationRequest.find(filters)
                    .populate('userId', 'firstName lastName email')
                    .populate('createdBy', 'firstName lastName')
                    .sort(sort)
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                AllocationRequest.countDocuments(filters),
            ]);

            const userIds = [...new Set(results.map(r => r.userId._id.toString()))];
            const labours = await Labour.find({ userId: { $in: userIds }, isDeleted: false })
                .select('userId employee_id skills')
                .lean();
            const labourMap = new Map(labours.map(l => [l.userId.toString(), l]));

            const requestsWithLabour = results.map(request => ({
                ...request,
                labour: labourMap.get(request.userId._id.toString()) || {},
            }));

            return {
                data: requestsWithLabour,
                total: totalCount,
                page,
                totalPage: Math.ceil(totalCount / limit),
            };

        } catch (error) {
            console.error('Error in getAllocationHistory:', error);
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('AllocationRequest.history') })
            );
        }
    }

    public async deleteMultipleAllocationRequests(ids: string[], updatedBy: string, t: TFunction) {

        const session = await mongoose.startSession();
        let transactionCommitted = false

        try {
            session.startTransaction()
            const allocationRequests = await AllocationRequest.find({
                _id: { $in: ids },
                isDeleted: false,
            })
                .populate('userId', 'firstName lastName email')
                .populate('createdBy', 'firstName lastName email')
                .populate('jobId', 'noOfSpots allotted open status companyName location')
                .session(session);

            if (!allocationRequests.length) {
                return { deleted: 0 };
            }

            const bulkOps = allocationRequests.map(request => ({
                updateOne: {
                    filter: { _id: request._id },
                    update: {
                        $set: {
                            isDeleted: true,
                            deletedAt: new Date(),
                            updatedBy: new Types.ObjectId(updatedBy),
                        },
                    },
                },
            }));
            await AllocationRequest.bulkWrite(bulkOps, { session });

            const userIds = allocationRequests.map(r => r.userId._id.toString());
            const labours = await Labour.find({ userId: { $in: userIds }, isDeleted: false })
                .select('userId employee_id')
                .session(session)
                .lean();
            const labourMap = new Map(labours.map(l => [l.userId.toString(), l.employee_id]));
            const updatedByUser = await User.findById(updatedBy)
                .select('firstName lastName email roleId')
                .populate('roleId', 'name')
                .session(session)
                .lean();
            if (!updatedByUser || !updatedByUser.roleId) {
                throw new HttpException(status.NotFound, t('General.not_found', { field: t('User.updatedBy') }));
            }

            const acceptedRequests = allocationRequests.filter(r => r.status.toLowerCase() === AllocationRequestStatusEnum.ACCEPTED.toLowerCase());
            if (acceptedRequests.length > 0) {
                const jobIds = [...new Set(acceptedRequests.map(r => r.jobId._id.toString()))];
                const jobs = await Job.find({ _id: { $in: jobIds } }).session(session);
                for (const job of jobs) {
                    const requestsForJob = acceptedRequests.filter(r => r.jobId._id.toString() === job._id.toString());
                    job.allotted -= requestsForJob.length;
                    job.open = job.noOfSpots - job.allotted;
                    if (job.open > 0 && job.status === 'Closed') {
                        job.status = 'Open';
                    }
                    await job.save({ session });
                }
            }
            await session.commitTransaction();
            transactionCommitted = true;
            const notificationErrors: Error[] = [];
            // await Promise.all(
            //     allocationRequests.map(async request => {
            //         await Labour.updateOne(
            //             { userId: request.userId, isDeleted: false },
            //             { $pull: { workInvitations: request._id } },
            //             { session }
            //         );

            //         await this.sendAllocationNotification(
            //             'deleteAllocationRequest',
            //             {
            //                 from: new Types.ObjectId(updatedBy),
            //                 to: [request.userId._id, request.createdBy._id],
            //                 entityData: {
            //                     userId: request.userId._id,
            //                     allocationRequestId: request._id,
            //                     location: (request.jobId as any).location,
            //                     wage: request.wage,
            //                     companyName: (request.jobId as any).companyName,
            //                     startDate: request.startDate,
            //                     endDate: request.endDate,
            //                     status: request.status,
            //                     labourId: labourMap.get(request.userId._id.toString()) || '',
            //                     firstName: (request.userId as any).firstName,
            //                     lastName: (request.userId as any).lastName,
            //                     responseNotes: request.responseNotes || '',
            //                 },
            //             },
            //             t
            //         );
            //     })
            // );

            // if ((updatedByUser.roleId as Role).name.toLowerCase() === RolesEnum.STAFF.toLowerCase()) {
            //     const adminUsers = await User.find({ isDeleted: false })
            //         .populate({
            //             path: 'roleId',
            //             match: { name: { $regex: new RegExp(`^${RolesEnum.ADMIN}$`, 'i') } },
            //             select: 'name',
            //         })
            //         .select('_id')
            //         .lean(); // No session for notifications
            //     const adminIds = adminUsers.filter(u => u.roleId).map(u => new Types.ObjectId(u._id));

            //     await Promise.all(
            //         allocationRequests.map(async request => {
            //             await this.sendAllocationNotification(
            //                 'deleteAllocationRequest',
            //                 {
            //                     from: new Types.ObjectId(updatedBy),
            //                     to: adminIds,
            //                     entityData: {
            //                         userId: request.userId._id,
            //                         allocationRequestId: request._id,
            //                         location: (request.jobId as any).location,
            //                         wage: request.wage,
            //                         companyName: (request.jobId as any).companyName,
            //                         startDate: request.startDate,
            //                         endDate: request.endDate,
            //                         status: request.status,
            //                         responseNotes: request.responseNotes || '',
            //                         labourId: labourMap.get(request.userId._id.toString()) || '',
            //                         firstName: (request.userId as any).firstName,
            //                         allocationNotes: request.allocationNotes || '',
            //                         lastName: (request.userId as any).lastName,
            //                     },
            //                 },
            //                 t
            //             );
            //         })
            //     );
            // }

            // if (notificationErrors.length > 0) {
            //     console.warn(`Some notifications failed: ${notificationErrors.length} errors`, notificationErrors);
            // }

            return { deleted: allocationRequests.length };

        } catch (error) {
            if (!transactionCommitted) {
                await session.abortTransaction();
            }
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.InternalServerError,
                t('General.errorDeleting', { field: t('AllocationRequest.allocationRequest') })
            );
        } finally {
            await session.endSession();
        }
    }



    public async getAllottedLabourers(jobId: string, page: number = 1, limit: number = 10, sort: any = { 'userId.firstName': 1 }, t: TFunction) {
        try {
            const skip = (page - 1) * limit;
            const filters = {
                jobId: new Types.ObjectId(jobId),
                status: AllocationRequestStatusEnum.ACCEPTED,
                isDeleted: false,
            };



            const [results, totalCount] = await Promise.all([
                AllocationRequest.find(filters)
                    .populate<{ userId: { _id: Types.ObjectId; firstName: string; lastName: string; email: string; profileImage: any } }>(
                        'userId',
                        'firstName lastName email profileImage'
                    )
                    .populate({
                        path: 'jobId',
                        select: 'srno'
                    })
                    .sort(sort)
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                AllocationRequest.countDocuments(filters),
            ]);

            const userIds = [...new Set(results.map(r => r.userId._id.toString()))];
            const labours = await Labour.find({ userId: { $in: userIds }, isDeleted: false })
                .select('userId employee_id')
                .lean();
            const labourMap = new Map(labours.map(l => [l.userId.toString(), l.employee_id]));

            const allottedLabourers = results.map(request => ({
                srno: (request.jobId as any).srno || null,
                employeeId: labourMap.get(request.userId._id.toString()) || null,
                firstName: (request.userId as any).firstName || 'Unknown',
                lastName: (request.userId as any).lastName || '',
                email: (request.userId as any).email || null,
                allocationRequestId: request._id.toString(),
                profileImage: request.userId.profileImage &&
                    'filename' in request.userId.profileImage &&
                    'key' in request.userId.profileImage &&
                    'fileSize' in request.userId.profileImage
                    ? {
                        _id: request.userId.profileImage._id.toString(),
                        filename: (request.userId.profileImage as { filename: string }).filename,
                        url: `${process.env.AWS_S3_BASE_URL}/${request.userId.profileImage.key}`,
                        fileSize: (request.userId.profileImage as { fileSize: number }).fileSize
                    }
                    : null,
                allocationNotes: request.allocationNotes || '',
            }));

            return {
                data: allottedLabourers,
                total: totalCount,
                page,
                totalPage: Math.ceil(totalCount / limit),
            };
        } catch (error) {
            if (error instanceof mongoose.Error.CastError) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'jobId' }));
            }
            throw error instanceof HttpException ? error : new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('AllocationRequest.AllocatedUser') })
            );
        }
    }

    public async getRequestedLabourers(jobId: string, page: number = 1, limit: number = 10, sort: any = { 'userId.firstName': 1 }, t: TFunction) {
        try {
            const skip = (page - 1) * limit;
            const filters = {
                jobId: new Types.ObjectId(jobId),
                status: { $in: [AllocationRequestStatusEnum.PENDING, AllocationRequestStatusEnum.REJECTED] },
                isDeleted: false,
            };

            const [results, totalCount] = await Promise.all([
                AllocationRequest.find(filters)
                    .populate<{ userId: { _id: Types.ObjectId; firstName: string; lastName: string; email: string; profileImage: any } }>(
                        'userId',
                        'firstName lastName email profileImage _id'
                    )
                    .populate({
                        path: 'jobId',
                        select: 'srno'
                    })
                    .sort(sort)
                    .skip(skip)
                    .limit(limit)
                    .lean(),
                AllocationRequest.countDocuments(filters),
            ]);


            const userIds = [...new Set(results.map(r => r.userId._id.toString()))];
            const labours = await Labour.find({ userId: { $in: userIds }, isDeleted: false })
                .select('userId employee_id')
                .lean();
            const labourMap = new Map(labours.map(l => [l.userId.toString(), l.employee_id]));

            const requestedLabourers = results.map(request => ({
                srno: (request.jobId as any).srno || null,
                employeeId: labourMap.get(request.userId._id.toString()) || null,
                firstName: (request.userId as any).firstName || 'Unknown',
                lastName: (request.userId as any).lastName || '',
                email: (request.userId as any).email || null,
                allocationRequestId: request._id.toString(),
                status: request.status,
                userId: request.userId._id,
                jobId: request.jobId,
                allocationNotes: request.allocationNotes || '',
                profileImage: request.userId.profileImage &&
                    'filename' in request.userId.profileImage &&
                    'key' in request.userId.profileImage &&
                    'fileSize' in request.userId.profileImage
                    ? {
                        _id: request.userId.profileImage._id.toString(),
                        filename: (request.userId.profileImage as { filename: string }).filename,
                        url: `${process.env.AWS_S3_BASE_URL}/${request.userId.profileImage.key}`,
                        fileSize: (request.userId.profileImage as { fileSize: number }).fileSize
                    }
                    : null,
            }));

            return {
                data: requestedLabourers,
                total: totalCount,
                page,
                totalPage: Math.ceil(totalCount / limit),
            };
        } catch (error) {
            if (error instanceof mongoose.Error.CastError) {
                throw new HttpException(status.BadRequest, t('General.invalid', { field: 'jobId' }));
            }
            throw error instanceof HttpException ? error : new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('AllocationRequest.requestedUser') })
            );
        }
    }


}